/**
 * PayablesByPartyPanel
 * --------------------
 * Every supplier and contractor we owe, side by side: billed, paid, still
 * outstanding and how much of that is late. Click a row for that party's
 * payment ledger — the same ledger the Suppliers dashboard opens.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, HardHat, Loader2, Search, TriangleAlert } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API } from "@/App";
import SupplierLedgerDialog from "@/components/admin/SupplierLedgerDialog";

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const VIEWS = [
  { id: "outstanding", label: "Outstanding" },
  { id: "overdue", label: "Overdue" },
  { id: "paid", label: "Fully paid" },
  { id: "all", label: "Everyone billing us" },
];

const KINDS = [
  { id: "all", label: "Suppliers & contractors" },
  { id: "supplier", label: "Suppliers" },
  { id: "contractor", label: "Contractors" },
  { id: "unlinked", label: "Unlinked names" },
];

const Tile = ({ label, value, tone = "slate", testid }) => (
  <div className={`rounded-lg border px-3 py-2 ${tone === "red" ? "border-red-200 bg-red-50"
    : tone === "amber" ? "border-amber-200 bg-amber-50"
    : tone === "emerald" ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"}`}
    data-testid={testid}>
    <p className="text-[9px] font-black uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`text-base font-black ${tone === "red" ? "text-red-700"
      : tone === "amber" ? "text-amber-800" : tone === "emerald" ? "text-emerald-700"
      : "text-slate-900"}`}>{value}</p>
  </div>
);

export const PayablesByPartyPanel = ({ token, refreshKey = 0 }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("outstanding");
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [ledgerFor, setLedgerFor] = useState(null);
  const [linking, setLinking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/invoice-audit/suppliers`, { headers });
      setRows(r.data?.suppliers || []);
      setSummary(r.data?.summary || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the payables by supplier");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, refreshKey]);

  useEffect(() => { load(); }, [load]);

  const linkParty = async (row) => {
    setLinking(row.name);
    try {
      const r = await axios.post(`${API}/invoice-audit/parties/link`,
        { billed_name: row.name, email: row.email || null }, { headers });
      toast.success(r.data.created ? `${row.name} added to Suppliers`
                                   : `${row.name} linked to the record on file`, {
        description: `${r.data.invoices_linked} invoice(s) and ${r.data.payments_linked} payment(s) now sit on their ledger` });
      await load();
      setLedgerFor({ id: r.data.supplier_id, name: r.data.name });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link that name");
    } finally { setLinking(null); }
  };

  const shown = rows.filter((r) => {
    if (kind !== "all" && r.party_type !== kind) return false;
    if (q.trim() && !(r.name || "").toLowerCase().includes(q.trim().toLowerCase())) return false;
    if (view === "outstanding") return r.open_count > 0;
    if (view === "overdue") return r.overdue_count > 0;
    if (view === "paid") return r.invoice_count > 0 && r.open_count === 0;
    return r.invoice_count > 0;
  });

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4"
      data-testid="payables-by-party">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-black text-slate-900 inline-flex items-center gap-2">
            <Building2 className="w-4 h-4 text-sky-600" /> Paid &amp; outstanding by supplier / contractor
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            What each party billed us, what we have paid them and what is still open.
            Click any row for their full payment ledger and printable statement.
          </p>
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            className="h-8 pl-8 text-xs w-52" placeholder="Supplier or contractor"
            data-testid="party-search" />
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mt-3">
          <Tile label="Outstanding" value={money(summary.total_open)} testid="party-tile-open" />
          <Tile label="Overdue" value={money(summary.total_overdue)} tone="red"
            testid="party-tile-overdue" />
          <Tile label="Paid to date" value={money(summary.total_paid)} tone="emerald"
            testid="party-tile-paid" />
          <Tile label="Parties with a balance" value={summary.with_open}
            tone={summary.with_overdue ? "amber" : "slate"} testid="party-tile-with-open" />
          <Tile label="Settled in full" value={summary.fully_paid} tone="emerald"
            testid="party-tile-settled" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mt-3">
        {VIEWS.map((v) => (
          <button key={v.id} onClick={() => setView(v.id)}
            className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${view === v.id
              ? "bg-sky-700 text-white border-sky-700" : "bg-white text-slate-600 border-slate-200"}`}
            data-testid={`party-view-${v.id}`}>{v.label}</button>
        ))}
        <span className="w-px h-5 bg-slate-200 mx-1" />
        {KINDS.map((k) => (
          <button key={k.id} onClick={() => setKind(k.id)}
            className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${kind === k.id
              ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}
            data-testid={`party-kind-${k.id}`}>{k.label}</button>
        ))}
      </div>

      <div className="overflow-x-auto mt-2">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-slate-50">
            <tr>
              {["Supplier / contractor", "Invoices", "Billed", "Paid", "Outstanding",
                "Overdue", "Terms", ""].map((h, i) => (
                <th key={`${h}-${i}`}
                  className="text-left px-2 py-1.5 text-[9.5px] font-black uppercase tracking-wider text-slate-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {shown.map((r) => (
              <tr key={`${r.party_type}-${r.id || r.name}`}
                className={r.overdue_count ? "bg-red-50/40" : ""}
                data-testid={`party-row-${r.id || r.name}`}>
                <td className="px-2 py-2">
                  <p className="font-bold text-slate-900 text-[12.5px] inline-flex items-center gap-1.5">
                    {r.party_type === "contractor"
                      ? <HardHat className="w-3.5 h-3.5 text-amber-600" />
                      : <Building2 className="w-3.5 h-3.5 text-slate-400" />}
                    {r.name || "Unnamed"}
                  </p>
                  <p className="text-[10.5px] text-slate-500">
                    {r.party_type === "unlinked"
                      ? <span className="text-amber-700 font-bold">
                          billed under a name with no record on file
                        </span>
                      : r.email || "—"}
                  </p>
                </td>
                <td className="px-2 py-2 text-[11px]">
                  {r.invoice_count}
                  <span className="block text-[10px] text-slate-500">
                    {r.paid_count} paid · {r.open_count} open
                  </span>
                </td>
                <td className="px-2 py-2 text-[11px] font-semibold">{money(r.lifetime_billed)}</td>
                <td className="px-2 py-2 text-[11px] font-semibold text-emerald-700">
                  {money(r.lifetime_paid)}
                  {r.last_payment_date && (
                    <span className="block text-[10px] text-slate-500 font-normal">
                      last {r.last_payment_date}
                    </span>
                  )}
                </td>
                <td className="px-2 py-2 text-[11px] font-black">{money(r.open_balance)}</td>
                <td className="px-2 py-2 text-[11px]">
                  {r.overdue_count ? (
                    <span className="font-black text-red-700 inline-flex items-center gap-1">
                      <TriangleAlert className="w-3.5 h-3.5" /> {money(r.overdue_balance)}
                      <span className="block text-[10px] font-bold">
                        {r.oldest_days_past_due}d
                      </span>
                    </span>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-2 py-2 text-[10.5px] text-slate-600">
                  {r.payment_terms || "—"}
                  {r.open_count > 0 && !r.has_bank_details && (
                    <span className="block text-[10px] text-amber-700 font-bold">no remit details</span>
                  )}
                </td>
                <td className="px-2 py-2 text-right">
                  {r.id ? (
                    <button onClick={() => setLedgerFor({ id: r.id, name: r.name })}
                      className="text-[11px] font-bold text-sky-700 hover:underline"
                      data-testid={`party-ledger-${r.id}`}>Payment ledger</button>
                  ) : (
                    <button onClick={() => linkParty(r)} disabled={linking === r.name}
                      className="text-[11px] font-bold text-amber-700 hover:underline disabled:opacity-50"
                      data-testid={`party-link-${r.name}`}>
                      {linking === r.name ? "Linking…" : "Create supplier record"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!shown.length && !loading && (
              <tr><td colSpan={8} className="px-2 py-5 text-center text-sm text-slate-400">
                Nothing in this view.
              </td></tr>
            )}
            {loading && (
              <tr><td colSpan={8} className="px-2 py-5 text-center text-sm text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin inline" /> Loading…
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <SupplierLedgerDialog open={!!ledgerFor} onClose={() => setLedgerFor(null)}
        supplierId={ledgerFor?.id} supplierName={ledgerFor?.name} token={token} />
    </section>
  );
};

export default PayablesByPartyPanel;
